// packages/supabase-client/src/react/forms/MagicLinkForm.tsx

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { Button, TextField } from '@dopomogai/ui';
import { validateEmail } from '../utils/validation';
import type { AuthFormData } from '../types/form';
import { useAuthText } from '../hooks';
import { AuthAlert } from '../components';

export interface MagicLinkFormProps {
  onSubmit: (data: Pick<AuthFormData, 'email'>) => Promise<void>;
  isPending?: boolean;
  errorMessage?: React.ReactNode | null;
  initialEmail?: string;
  onEmailChange?: (email: string) => void;
}

export const MagicLinkForm: React.FC<MagicLinkFormProps> = ({
  onSubmit,
  isPending,
  errorMessage,
  initialEmail,
  onEmailChange,
}) => {
  const { t } = useTranslation(['auth', 'common']);
  const { text, resolveText, resolveString } = useAuthText();
  const [email, setEmail] = useState(initialEmail ?? '');
  const [error, setError] = useState<string | undefined>();
  const sendText = resolveText(text.sendMagicLinkButton, 'auth:sendMagicLinkButton');
  const sendTextIsString = typeof sendText === 'string' || typeof sendText === 'number';

  useEffect(() => {
    if (initialEmail !== undefined) {
      setEmail(initialEmail);
    }
  }, [initialEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailError = validateEmail(email);
    if (emailError) {
      setError(t(emailError, { ns: 'common' }));
      return;
    }
    setError(undefined);
    await onSubmit({ email });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errorMessage && <AuthAlert message={errorMessage} />}
      <TextField
        label={resolveString(text.emailLabel, 'common:email')}
        value={email}
        onChange={(e) => {
          const nextEmail = e.target.value;
          setEmail(nextEmail);
          onEmailChange?.(nextEmail);
        }}
        placeholder={resolveString(text.emailPlaceholder, 'auth:emailPlaceholder')}
        leadingIcon={<Mail className="w-5 h-5 text-slate-400" />}
        errorText={error}
        disabled={isPending}
      />
      <Button
        type="submit"
        variant="primary"
        fullWidth
        text={sendTextIsString ? String(sendText) : undefined}
        disabled={isPending}
        loading={isPending}
      >
        {!sendTextIsString ? sendText : null}
      </Button>
    </form>
  );
};
